// src/components/tour/tourStore.js
//
// Tiny global store that connects a PAGE (which owns the live data) with
// FleetHeader (which owns the "?" button). No provider needed in App.jsx.
//
// A page calls:
//     usePageTour('device-manager', { ...liveData }, { ready: !loading });
//
// FleetHeader calls useActivePageTour() to know whether to show "?",
// and getActiveTourContext() at click time to read the freshest data.

import { useEffect, useRef, useSyncExternalStore } from 'react';

let active = null;              // { token, id, ready, getContext }
let snapshot = { id: null, ready: false };
const listeners = new Set();

function emit() {
    const next = { id: active?.id ?? null, ready: !!active?.ready };
    if (next.id !== snapshot.id || next.ready !== snapshot.ready) {
        snapshot = next;
        listeners.forEach((l) => l());
    }
}

function subscribe(listener) {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

/** Used by pages: register this page's tour + live data. */
export function usePageTour(id, context, { ready = true } = {}) {
    const ctxRef = useRef(context);
    ctxRef.current = context; // always the latest render's data

    useEffect(() => {
        const token = {};
        active = { token, id, ready, getContext: () => ctxRef.current };
        emit();
        return () => {
            // Only clear if a newer page hasn't already replaced us.
            if (active?.token === token) {
                active = null;
                emit();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        if (active && active.id === id) {
            active.ready = ready;
            emit();
        }
    }, [id, ready]);
}

/** Used by FleetHeader: { id, ready } of the page currently on screen. */
export function useActivePageTour() {
    return useSyncExternalStore(subscribe, () => snapshot, () => snapshot);
}

/** Latest live data of the active page (read when "?" is pressed). */
export function getActiveTourContext() {
    return active ? active.getContext() : null;
}
